import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip, { type ChipProps } from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { type GridColDef } from '@mui/x-data-grid-premium'
import { FulfillmentOrderStatus, TransferOrderStatus, useFulfillmentOrderCancellationStatusQuery } from '../../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'
import { useFulfillmentOrderCancellation } from '../RequestDetails.hooks'
import { DRILL_DOWN_CARET_INDENT } from '../RequestDetails.constants'
import { STATUS_LABEL } from '../../transfer-orders/constants'
import { FulfillmentLineItemResult, LineItem } from '../../../types/demandRequest'
import DataGrid from '../../components/DataGrid'

type FoLineItem = LineItem['fulfillmentOrderLineItems'][0]
type ToLineItem = LineItem['transferOrderLineItems'][0]

type FoRow = { _rowId: string; kind: 'fo'; foli: FoLineItem }
type CartonPlaceholderRow = { _rowId: string; kind: 'fo-placeholder' }
type CartonRow = { _rowId: string; kind: 'carton'; fulfillmentLineItem: FulfillmentLineItemResult }
type ToRow = { _rowId: string; kind: 'to'; toli: ToLineItem }
type ArtifactRow = FoRow | CartonPlaceholderRow | CartonRow | ToRow

type FoBadgeColor = NonNullable<ChipProps['color']>

// Class applied to the nested carton / placeholder rows so they read as
// subordinate to the FO row above them (see the grid `sx` below).
const NESTED_ROW_CLASS = 'SourcingArtifactTable-nestedRow'

type Props = {
	lineItem: LineItem,
	canCancel: boolean,
}

function formatDate( value: unknown ): string {
	if ( !value ) return '—'
	const date = new Date( value as string )
	if ( Number.isNaN( date.getTime() ) ) return '—'
	return date.toLocaleDateString( 'en-US', { month: 'short', day: 'numeric', year: 'numeric' } )
}

function foStatusBadge( foli: FoLineItem ): React.ReactNode {
	const foStatus = foli.fulfillmentOrder.status

	if ( foStatus === FulfillmentOrderStatus.Completed ) {
		if ( foli.isFullyFulfilled ) return <Chip color="success" label="Fulfilled" />
		if ( foli.quantityFulfilled > 0 ) return <Chip color="warning" label="Partially Fulfilled" />
		return <Chip color="error" label="Not Fulfilled" />
	}
	if ( foStatus === FulfillmentOrderStatus.Cancelled ) {
		return <Chip color="default" label="Cancelled" />
	}
	if ( foli.fulfillmentLineItems.length > 0 ) return <Chip color="primary" label="In Progress" />
	return <Chip color="default" label="Pending" />
}

const FO_STATUS_COLOR: Record<FulfillmentOrderStatus, FoBadgeColor> = {
	[FulfillmentOrderStatus.Pending]: 'default',
	[FulfillmentOrderStatus.SubmittedForFulfillment]: 'primary',
	[FulfillmentOrderStatus.Completed]: 'success',
	[FulfillmentOrderStatus.CancellationRequested]: 'warning',
	[FulfillmentOrderStatus.Cancelled]: 'default',
}

const FO_STATUS_LABEL: Record<FulfillmentOrderStatus, string> = {
	[FulfillmentOrderStatus.Pending]: 'Pending',
	[FulfillmentOrderStatus.SubmittedForFulfillment]: 'In Progress',
	[FulfillmentOrderStatus.Completed]: 'Fulfilled',
	[FulfillmentOrderStatus.CancellationRequested]: 'Cancelling…',
	[FulfillmentOrderStatus.Cancelled]: 'Cancelled',
}

const TO_STATUS_COLOR: Record<TransferOrderStatus, FoBadgeColor> = {
	[TransferOrderStatus.Pending]: 'warning',
	[TransferOrderStatus.Approved]: 'primary',
	[TransferOrderStatus.Rejected]: 'error',
	[TransferOrderStatus.InTransit]: 'primary',
	[TransferOrderStatus.Completed]: 'success',
	[TransferOrderStatus.Cancelled]: 'default',
}

function toStatusBadge( status: TransferOrderStatus | null | undefined ): React.ReactNode {
	if ( !status ) return null
	return <Chip color={TO_STATUS_COLOR[ status ]} label={STATUS_LABEL[ status ]} />
}

function isSubstitution( artifactPartNumber: string | null | undefined, lineItemPartNumber: string | null | undefined ): boolean {
	if ( !artifactPartNumber || !lineItemPartNumber ) return false
	return artifactPartNumber !== lineItemPartNumber
}

function SubstitutionBadge( { originalProduct }: { originalProduct: LineItem['product'] } ) {
	const label = originalProduct.partNumber
		? `${originalProduct.partNumber} — ${originalProduct.name}`
		: originalProduct.name
	return <Chip color="warning" label="Sub" title={`Substitutes: ${label}`} sx={{ ml: 1 }} />
}

// Live FO status badge backed by the standalone fulfillmentOrder query. Renders
// an in-progress indicator while CANCELLATION_REQUESTED (independent of any
// active poll) and a persistent failure indicator when cancellationFailed.
//
// The live query writes only the FO's status into the shared cache entry, while
// the quantities and cartons beside it still come from the request's query. If
// the FO moved on since the request loaded (e.g. its fulfillment completed), the
// row would pair the new status with the old quantities and read as shorted, so
// the request is refetched to bring the rest of the row up to date.
function FulfillmentOrderStatusBadge( { fulfillmentOrderId, loadedStatus, fallback }: { fulfillmentOrderId: string, loadedStatus: FulfillmentOrderStatus, fallback: React.ReactNode } ) {
	const client = useApolloClient()
	const { data } = useFulfillmentOrderCancellationStatusQuery( {
		variables: { fulfillmentOrderId },
		fetchPolicy: 'cache-and-network',
	} )
	const fo = data?.fulfillmentOrder

	const knownStatus = useRef( loadedStatus )
	useEffect( () => {
		if ( !fo || fo.status === knownStatus.current ) {
			return
		}
		knownStatus.current = fo.status
		client.refetchQueries( { include: [ 'DemandRequest' ] } )
	}, [ client, fo ] )

	if ( !fo ) {
		return <>{fallback}</>
	}

	if ( fo.status === FulfillmentOrderStatus.CancellationRequested ) {
		return (
			<Chip
				color="warning"
				icon={<CircularProgress size={12} color="inherit" />}
				label={FO_STATUS_LABEL[ FulfillmentOrderStatus.CancellationRequested ]}
			/>
		)
	}

	// Stack vertically so both the underlying status and the failure indicator
	// fit within the fixed-width Status column instead of bleeding into Date.
	return (
		<Stack direction="column" spacing={0.5} alignItems="flex-start">
			<Chip color={FO_STATUS_COLOR[ fo.status ]} label={FO_STATUS_LABEL[ fo.status ]} />
			{fo.cancellationFailed && (
				<Tooltip title={fo.cancellationFailureMessage ?? 'Cancellation failed.'}>
					<Chip color="error" icon={<WarningAmberIcon />} label="Cancel failed" />
				</Tooltip>
			)}
		</Stack>
	)
}

// Action-cell control: requests cancellation and reflects the async outcome.
// While CANCELLATION_REQUESTED, no action is offered (AC: no actions while in
// that state); past the fast-path timeout it surfaces a non-blocking warning.
function CancelFulfillmentOrderControl( { fulfillmentOrderId }: { fulfillmentOrderId: string } ) {
	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>( null )
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const { status, phase, tookTooLong, isInProgress, submit } = useFulfillmentOrderCancellation( {
		fulfillmentOrderId,
		enabled: true,
		onSucceeded: () => showSuccess( `cancelFo-${fulfillmentOrderId}`, 'Fulfillment order cancelled.' ),
		onFailed: ( message ) => showError( `cancelFo-${fulfillmentOrderId}`, message ?? 'Cancellation failed.' ),
	} )

	// Terminal states offer no action.
	if ( status === FulfillmentOrderStatus.Cancelled || status === FulfillmentOrderStatus.Completed || phase === 'succeeded' ) {
		return null
	}

	// In progress: the status badge already shows the spinner; only surface the
	// non-blocking "taking longer" warning here.
	if ( isInProgress ) {
		return tookTooLong
			? <Typography variant="inherit" color="warning.main">Cancelling is taking longer than expected…</Typography>
			: null
	}

	const handleConfirm = () => {
		setAnchorEl( null )
		submit()
	}

	return (
		<>
			<Button
				variant="text"
				size="small"
				color="error"
				loading={phase === 'requesting'}
				onClick={( event ) => setAnchorEl( ( prev ) => ( prev ? null : event.currentTarget ) )}
			>
				Cancel
			</Button>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl( null )}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<Box sx={{ p: 1 }}>
					<Typography variant="body2">Request cancellation of this fulfillment order?</Typography>
					<Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
						<Button variant="text" size="small" onClick={() => setAnchorEl( null )}>Back</Button>
						<Button variant="contained" size="small" color="error" onClick={handleConfirm}>Confirm</Button>
					</Stack>
				</Box>
			</Popover>
		</>
	)
}

export default function SourcingArtifactTable( { lineItem, canCancel }: Props ) {
	const { fulfillmentOrderLineItems, transferOrderLineItems } = lineItem

	const rows = useMemo<ArtifactRow[]>( () => {
		const result: ArtifactRow[] = []

		fulfillmentOrderLineItems.forEach( ( foli ) => {
			result.push( { _rowId: `fo-${foli.id}`, kind: 'fo', foli } )
			if ( foli.fulfillmentLineItems.length === 0 ) {
				result.push( { _rowId: `placeholder-${foli.id}`, kind: 'fo-placeholder' } )
			} else {
				foli.fulfillmentLineItems.forEach( ( fulfillmentLineItem ) => {
					result.push( { _rowId: `carton-${fulfillmentLineItem.fulfillment.id}`, kind: 'carton', fulfillmentLineItem } )
				} )
			}
		} )

		transferOrderLineItems.forEach( ( toli ) => {
			result.push( { _rowId: `to-${toli.lineItemId}`, kind: 'to', toli } )
		} )

		return result
	}, [ fulfillmentOrderLineItems, transferOrderLineItems ] )

	if ( rows.length === 0 ) {
		return (
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ my: 0.5, ml: `${DRILL_DOWN_CARET_INDENT}px`, borderLeft: 2, borderColor: 'divider', px: 2, py: 0.5 }}
			>
				No sourcing activity yet
			</Typography>
		)
	}

	const columns: GridColDef<ArtifactRow>[] = [
		{
			field: 'source',
			headerName: 'Source',
			width: 190,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( row.kind === 'fo' ) {
					// Which DC filled this. One request can be sourced from several, so
					// the fulfilling location is part of the answer to "where did this
					// come from", not a detail of the order.
					const fulfilledBy = row.foli.fulfillmentOrder.fulfillingInventory?.name
					return (
						<Box>
							<Typography variant="inherit" component="strong">FO-{row.foli.fulfillmentOrder.orderNumber}</Typography>
							{fulfilledBy && (
								<Typography variant="caption" color="text.secondary" component="div">{fulfilledBy}</Typography>
							)}
						</Box>
					)
				}
				if ( row.kind === 'carton' || row.kind === 'fo-placeholder' ) {
					return <Typography variant="inherit" color="text.secondary">↳ Carton</Typography>
				}
				const toId = row.toli.transferOrder?.id
				if ( !toId ) return <Typography variant="inherit" color="text.secondary">—</Typography>
				return (
					<Link component={RouterLink} to={`/transfer-orders/${toId}`} variant="inherit">
						<Typography variant="inherit" component="strong">TO-{toId}</Typography>
					</Link>
				)
			},
		},
		{
			field: 'product',
			headerName: 'Product',
			flex: 1,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( row.kind === 'fo' ) {
					const sub = isSubstitution( row.foli.product.partNumber, lineItem.product.partNumber )
					return (
						<Typography variant="inherit">
							{row.foli.product.partNumber ? `${row.foli.product.partNumber} — ` : ''}{row.foli.product.name}
							{sub && <SubstitutionBadge originalProduct={lineItem.product} />}
						</Typography>
					)
				}
				if ( row.kind === 'carton' ) {
					const { trackingNumber } = row.fulfillmentLineItem.fulfillment
					return trackingNumber
						? <Typography variant="inherit">{`Tracking: ${trackingNumber}`}</Typography>
						: <Typography variant="inherit" color="text.secondary">—</Typography>
				}
				// A fulfillment order with nothing packed yet. That it was submitted is
				// already said by the order's own row above, so this one stays empty
				// until a carton fills it — as Qty, Status and Date already do.
				if ( row.kind === 'fo-placeholder' ) {
					return null
				}
				// TO artifacts expose a GoodProduct, which only carries manufacturerNumber.
				// Compare against the line item Part's manufacturerNumber (not partNumber) so
				// we're matching the same identifier scheme on both sides.
				const productNumber = row.toli.product?.manufacturerNumber
				const productName = row.toli.product?.name ?? ''
				const sub = productNumber
					? isSubstitution( productNumber, lineItem.product.manufacturerNumber )
					: false
				return (
					<Typography variant="inherit">
						{productNumber ? `${productNumber} — ` : ''}{productName}
						{sub && <SubstitutionBadge originalProduct={lineItem.product} />}
					</Typography>
				)
			},
		},
		{
			field: 'qty',
			headerName: 'Qty',
			width: 140,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( row.kind === 'fo' ) {
					const { quantity, quantityFulfilled, fulfillmentOrder } = row.foli
					const foStatus = fulfillmentOrder.status
					if ( foStatus === FulfillmentOrderStatus.Completed ) {
						if ( row.foli.isFullyFulfilled ) {
							return <Typography variant="inherit">{quantity} fulfilled</Typography>
						}
						if ( quantityFulfilled > 0 ) {
							return <Typography variant="inherit">{quantityFulfilled} fulfilled, {quantity - quantityFulfilled} shorted</Typography>
						}
						return <Typography variant="inherit">{quantity} shorted</Typography>
					}
					const remaining = quantity - quantityFulfilled
					if ( quantityFulfilled > 0 ) {
						return <Typography variant="inherit">{quantityFulfilled} fulfilled, {remaining} remaining</Typography>
					}
					return <Typography variant="inherit">{quantity}</Typography>
				}
				if ( row.kind === 'to' ) return <Typography variant="inherit">{row.toli.quantityRequested}</Typography>
				return null
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 120,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( row.kind === 'fo' ) {
					const fallback = foStatusBadge( row.foli )
					return canCancel
						? <FulfillmentOrderStatusBadge fulfillmentOrderId={row.foli.fulfillmentOrderId} loadedStatus={row.foli.fulfillmentOrder.status} fallback={fallback} />
						: fallback
				}
				if ( row.kind === 'carton' ) {
					const { quantityFulfilled, quantityUnfulfilled } = row.fulfillmentLineItem
					if ( quantityFulfilled > 0 ) return <Chip color="success" label="Shipped" />
					if ( quantityUnfulfilled > 0 ) return <Chip color="error" label="Shorted" />
					return <Chip color="default" label="Preparing" />
				}
				if ( row.kind === 'to' ) {
					return toStatusBadge( row.toli.transferOrder?.status )
				}
				return null
			},
		},
		{
			field: 'date',
			headerName: 'Date',
			width: 120,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( row.kind === 'fo' ) {
					return <Typography variant="inherit" color="text.secondary">{formatDate( row.foli.fulfillmentOrder.dateSubmittedForFulfillment ?? row.foli.fulfillmentOrder.dateCreated )}</Typography>
				}
				if ( row.kind === 'carton' ) {
					return <Typography variant="inherit" color="text.secondary">{formatDate( row.fulfillmentLineItem.fulfillment.dateShipped )}</Typography>
				}
				if ( row.kind === 'to' ) {
					return <Typography variant="inherit" color="text.secondary">{formatDate( row.toli.transferOrder?.dateCreated )}</Typography>
				}
				return null
			},
		},
		{
			field: 'actions',
			headerName: '',
			width: 160,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( row.kind === 'fo' && !row.foli.isFullyFulfilled && canCancel ) {
					return <CancelFulfillmentOrderControl fulfillmentOrderId={row.foli.fulfillmentOrderId} />
				}
				// A fulfilled FO spawns a system transfer order; surface it here since the
				// FO row itself shows the FO number, not the downstream TO.
				if ( row.kind === 'carton' ) {
					const { systemTransferOrder } = row.fulfillmentLineItem.fulfillment
					if ( !systemTransferOrder ) return null
					const qty = row.fulfillmentLineItem.quantityFulfilled
					const label = qty > 0 ? `TO-${systemTransferOrder.id} · ${qty} item${qty === 1 ? '' : 's'}` : `TO-${systemTransferOrder.id}`
					return <Link component={RouterLink} to={`/transfer-orders/${systemTransferOrder.id}`} variant="inherit">{label}</Link>
				}
				return null
			},
		},
	]

	return (
		<Box sx={{ my: 0.5, ml: `${DRILL_DOWN_CARET_INDENT}px`, borderLeft: 2, borderColor: 'divider', maxWidth: 880 }}>
			<DataGrid
				columns={columns}
				rows={rows}
				getRowId={( row ) => row._rowId}
				getRowHeight={() => 'auto'}
				getRowClassName={( { row } ) => (
					( row.kind === 'carton' || row.kind === 'fo-placeholder' ) ? NESTED_ROW_CLASS : ''
				)}
				sx={{
					// The drill-down reads as secondary to the line-item row that owns it,
					// so its type is a step down from the parent table's.
					'fontSize': '0.75rem',
					'& .MuiDataGrid-cell': { whiteSpace: 'normal', py: 0.5 },
					[ `& .${NESTED_ROW_CLASS}` ]: { bgcolor: 'action.hover' },
				}}
			/>
		</Box>
	)
}
